import React from 'react';
import Grid from '@mui/material/Grid';
import DataDisplay from './DataDisplay';

/**
 * @param {Array<Object>} descriptions
 * @param {Object} [descriptions[].actionButton]
 * @param {string} [descriptions[].key]
 * @param {string} [descriptions[].label]
 * @param {Function} [descriptions[].formatter]
 * @param {Object} [descriptions[].component]
 * @param {Object} [descriptions[].additionalProps]
 * @param {Function} [descriptions[].skipWhen]
 * @param {string} [descriptions[].editKey]
 * @param {Function} [descriptions[].editor] - See components using 'editorProps' for examples
 * @param {boolean} [descriptions[].readOnly]
 * @param {Function} [descriptions[].validator]
 * @param {Object<string, *>} [data]
 * @param {Function} [updateValue] - (key, val) =>; works with 'update' action in dataReducer
 * @param {Object<string, *>} [updatedData={}]
 * @param {boolean} [editing=false]
 * @param {React.ReactNode} [fallback='Data not available']
 * @param {boolean} [copyable=false]
 * @param {boolean} [multiline=false]
 * @param {number|Object} [size=12]
 */
// Formats fields of data into list group items.
// Supports updating via the editor/setValue props.
export function DataFormatter({
  descriptions,
  editing = false,
  data,
  fallback = 'Data not available',
  updateValue = () => null,
  updatedData = {},
  copyable = false,
  multiline = false,
  size = 12,
}) {
  if (data === undefined) {
    return null;
  }

  if (data === null) {
    return fallback;
  }
  return (
    <Grid container spacing={1} size={12}>
      {descriptions.map(({
        actionButton,
        editInline,
        editKey,
        editor: Editor,
        readOnly,
        formatter,
        component,
        key,
        label,
        skipWhen,
        validator,
        ...additionalProps
      }) => {
        if (skipWhen && skipWhen(data)) return null;

        const displayValue = formatter ? formatter(data) : data[key];

        if ((!editing && !editInline) || readOnly) {
          return (
            <DataDisplay
              key={label}
              value={component || displayValue}
              label={label}
              action={actionButton}
              copyable={copyable}
              multiline={multiline}
            />
          );
        }

        if (!Editor) {
          return null;
        }

        const editorKey = editKey || key;

        // Change null values to undefined which causes the editor's default prop to set its value.
        const dataValue = data[editorKey] === null ? undefined : data[editorKey];
        const editValue = updatedData[editorKey] === undefined ? dataValue : updatedData[editorKey];

        return (
          <Grid key={key} size={size} sx={{ marginY: '5px' }}>
            <Editor
              setValue={(newValue) => updateValue(editorKey, newValue)}
              value={editValue}
              label={label}
              validator={validator}
              {...additionalProps}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

export function DataFormatterWithColumns({
  descriptions,
  numberOfColumns,
  ...rest
}) {
  const columnSize = Math.floor(12 / numberOfColumns);

  return (
    <Grid container columnSpacing={1} sx={{ justifyContent: 'space-between' }}>
      {descriptions.map((d) => (
        <Grid size={{ xs: 12, sm: columnSize }} key={d.label || d.key}>
          <DataFormatter descriptions={[d]} {...rest} />
        </Grid>
      ))}
    </Grid>
  );
}
